/// <reference types="vite-plugin-svgr/client" />
import { Stack, Typography, Theme } from '@mui/material';
import { useState, useCallback, useEffect, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PhoneForInstructionForm } from './components/PhoneForInstructionForm/PhoneForInstructionForm';
import { PhoneVerificationForm } from '../../../components/forms/PhoneVerificationForm/PhoneVerificationForm';
import { AuthService } from '../../../services/auth/AuthService';
import { ROUTES } from '../../../constants/routes';
import { AuthTabs } from '../../../common/enums/auth';
import { AuthDeliveryChannel } from '@shared/enums/auth-delivery-channel.enum';
import { LoginForm } from '../../../components/forms/LoginForm/LoginForm';
import { sendEventToReactNative } from '../../../common/utils/reactNativeHelpers';
import { WebViewEvents } from '../../../common/enums/webViewEvents';
import { BackButton } from '../../../components/common/Button/BackButton/BackButton';
import { useUser, useUserActions } from '../../../hooks/useUser';
import { localStorageService } from '../../../services/localStorageService/localStorageService';
import { usePhoneCodeDelivery } from '../../../hooks/usePhoneCodeDelivery';
import { AuthOutcome } from '../../../components/forms/LoginForm/types';

const authService = new AuthService();

/**
 * Single source of truth for what the auth screen is showing. Replaces the
 * previous set of correlated boolean flags (which allowed invalid combinations).
 */
type AuthFlow =
  | { step: 'form' }
  | { step: 'forgot' }
  | { step: 'verify'; kind: AuthOutcome['kind'] };

export const LoginPage = () => {
  const [activeTab, setActiveTab] = useState<AuthTabs>(AuthTabs.SIGN_IN);
  const [flow, setFlow] = useState<AuthFlow>({ step: 'form' });
  const {
    deliveredVia,
    availableChannels,
    apply: applyDelivery,
    reset: resetDelivery,
  } = usePhoneCodeDelivery();
  const { user } = useUser();
  const { fetchUser } = useUserActions();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const isVerify = flow.step === 'verify';
  const isReturningUser = isVerify && flow.kind === 'signin';

  // Accept SetStateAction<boolean> so it satisfies the Dispatch<SetStateAction<boolean>>
  // props it is passed to, while mapping the boolean onto the auth flow state.
  const setForgot = useCallback((value: SetStateAction<boolean>) => {
    setFlow((prev) => {
      const isForgot =
        typeof value === 'function' ? value(prev.step === 'forgot') : value;
      return isForgot ? { step: 'forgot' } : { step: 'form' };
    });
  }, []);

  const handleAuthOutcome = useCallback(
    (outcome: AuthOutcome) => {
      applyDelivery(outcome);
      setFlow({ step: 'verify', kind: outcome.kind });
    },
    [applyDelivery],
  );

  const handleBackClick = useCallback(() => {
    setFlow({ step: 'form' });
    resetDelivery();
  }, [resetDelivery]);

  const handleSelectChannel = useCallback(
    async (channel: AuthDeliveryChannel) => {
      if (!user?.phone) return;
      const result = isReturningUser
        ? await authService.resendSignInCode(user.phone, channel)
        : await authService.resendCode(user.phone, channel);
      applyDelivery(result);
    },
    [user?.phone, isReturningUser, applyDelivery],
  );

  const phoneVerificationProps = {
    offBoxShadow: false,
    isReturningUser,
    descriptionText: isReturningUser ? (
      <>
        {t('userAuth.phoneConfirmation.signInText')}
        <br />
        <br />
        {t('userAuth.phoneConfirmation.textPart3')}
      </>
    ) : (
      <>
        {t('userAuth.phoneConfirmation.textPart1')}
        {t('userAuth.phoneConfirmation.textPart2')}
        <br />
        <br />
        {t('userAuth.phoneConfirmation.textPart3')}
      </>
    ),
    onResendCode: async () => {
      if (!user?.phone) return;
      if (isReturningUser) {
        await authService.resendSignInCode(user.phone);
      } else {
        await authService.resendCode(user.phone);
      }
    },
  };

  const backButton =
    user?.phone && isVerify ? (
      <Stack sx={{ position: 'absolute', top: '6%', left: '22px', zIndex: 1 }}>
        <BackButton skipReloadPage customClickFn={handleBackClick} />
      </Stack>
    ) : null;

  let content;

  if (flow.step === 'verify') {
    content = (
      <>
        {backButton}
        <PhoneVerificationForm
          {...phoneVerificationProps}
          goToCabinet={true}
          deliveredVia={deliveredVia}
          availableChannels={availableChannels}
          onSelectChannel={handleSelectChannel}
          onSubmit={async (code: string) => {
            if (!user?.phone) return;
            if (isReturningUser) {
              await authService.phoneSignInVerify(user.phone, code);
            } else {
              await authService.completeSignUp(user.phone, code);
            }
            // Fetch user data to update the authentication state
            await fetchUser();

            const redirectUrl = localStorageService().getRedirectUrl();
            if (redirectUrl) {
              localStorageService().removeRedirectUrl();
              navigate(redirectUrl);
            } else {
              navigate(`/${ROUTES.CABINET}`);
            }
          }}
        />
      </>
    );
  } else if (flow.step === 'forgot') {
    content = (
      <PhoneForInstructionForm
        setActiveTab={setActiveTab}
        setIsForgotPassword={setForgot}
      />
    );
  } else {
    content = (
      <Stack
        sx={(theme: Theme) => ({
          background: theme.palette.gray[300],
          boxShadow: `0px -32px 30px 15px ${theme.palette.gray[300]}`,
          maxWidth: '400x',
          width: '100%',
        })}
      >
        <Typography variant="h1" color="green.main" textAlign="center">
          {activeTab === AuthTabs.SIGN_IN ? (
            t('userAuth.greetingSingInTitle')
          ) : (
            <>
              {t('userAuth.greetingSingUpTitle1')} <br />
              {t('userAuth.greetingSingUpTitle2')}
            </>
          )}
        </Typography>
        <LoginForm
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setIsForgotPassword={setForgot}
          onAuthOutcome={handleAuthOutcome}
        />
      </Stack>
    );
  }

  useEffect(() => {
    sendEventToReactNative(WebViewEvents.CHECK_AUTO_BIOMETRIC_AUTH);
  }, []);

  return content;
};
